import {
  CreateVehicleFinancingTemplateRequestBody,
  RateMatrixRequestBody,
  VehicleFinancingBalloonPayment,
  VehicleFinancingDownPayment,
  VehicleFinancingTerm,
  VehicleSegmentRequestBody,
} from '@dms/api/sales';

import {Nullish} from 'shared/web';

import {VehicleFinancingTemplateFormValues} from '../schemas/vehicleFinancingTemplateFormSchema';
import {numberToMoney} from './numberToMoney';
import {TemplateMoneyCurrencies} from './resolveTemplateMoneyCurrencies';

interface MapFormValuesToRequestBodyOptions {
  rateMatrix: RateMatrixRequestBody;
  defaultBalloonPayment: VehicleFinancingBalloonPayment | Nullish;
  defaultTerm: VehicleFinancingTerm | Nullish;
  defaultDownPayment: VehicleFinancingDownPayment | Nullish;
  moneyCurrencies: TemplateMoneyCurrencies;
}

const EMPTY_SEGMENT_REQUEST_BODY: VehicleSegmentRequestBody = {
  makes: [],
  models: [],
  vehicleCategories: [],
  fuelTypes: [],
  yearFrom: null,
  yearTo: null,
  mileageFrom: null,
  mileageTo: null,
  sellingPriceFrom: null,
  sellingPriceTo: null,
};

export function mapFormValuesToRequestBody(
  values: VehicleFinancingTemplateFormValues,
  options: MapFormValuesToRequestBodyOptions
): CreateVehicleFinancingTemplateRequestBody {
  // `type` and `downPaymentStep` are `.min(1)` chip arrays, so the form never submits them empty
  // and the backend requires both as scalars. The optional chip arrays below map an empty
  // selection to the `null` the wire contract expects.
  const type = values.type[0];
  const balloonPaymentStep = type === 'BALLOON' ? (values.balloonPaymentStep[0] ?? null) : null;
  const hasEndDate = values.validTo !== null;

  return {
    name: values.name,
    validFrom: values.validFrom,
    validTo: values.validTo,
    expirationAction: hasEndDate ? values.expirationAction : null,
    replacementTemplateId:
      hasEndDate && values.expirationAction === 'REPLACE' ? values.replacementTemplateId : null,
    type,
    interestRateType: values.interestRateType[0] ?? null,
    financialInstitutionCodeId: values.financialInstitutionCodeId,
    oneTimeCosts: {
      processingFee: numberToMoney(values.processingFee, options.moneyCurrencies.processingFee),
      vehicleTransfer: {
        included: values.vehicleTransferIncluded,
        amount: numberToMoney(
          values.vehicleTransferAmount,
          options.moneyCurrencies.vehicleTransferAmount
        ),
      },
      vehicleRegistration: {
        included: values.vehicleRegistrationIncluded,
        amount: numberToMoney(
          values.vehicleRegistrationAmount,
          options.moneyCurrencies.vehicleRegistrationAmount
        ),
      },
      vehicleDelivery: {
        included: values.vehicleDeliveryIncluded,
        amount: numberToMoney(
          values.vehicleDeliveryAmount,
          options.moneyCurrencies.vehicleDeliveryAmount
        ),
      },
    },
    tradeInPossible: values.tradeInPossible,
    creditProtectionAutoEnable: values.creditProtectionAutoEnable,
    scope: {
      mode: values.scope.mode,
      branchIds: values.scope.mode === 'BRANCH' ? values.scope.branchIds : [],
      segment:
        values.scope.mode === 'SEGMENT'
          ? {
              makes: values.scope.segment.makes,
              models: values.scope.segment.models,
              vehicleCategories: values.scope.segment.vehicleCategories,
              fuelTypes: values.scope.segment.fuelTypes,
              yearFrom: values.scope.segment.yearFrom,
              yearTo: values.scope.segment.yearTo,
              mileageFrom: values.scope.segment.mileageFrom,
              mileageTo: values.scope.segment.mileageTo,
              sellingPriceFrom: numberToMoney(
                values.scope.segment.sellingPriceFrom,
                options.moneyCurrencies.sellingPriceFrom
              ),
              sellingPriceTo: numberToMoney(
                values.scope.segment.sellingPriceTo,
                options.moneyCurrencies.sellingPriceTo
              ),
            }
          : EMPTY_SEGMENT_REQUEST_BODY,
    },
    downPaymentStep: values.downPaymentStep[0],
    balloonPaymentStep,
    defaultBalloonPayment: type === 'BALLOON' ? (options.defaultBalloonPayment ?? null) : null,
    defaultTerm: options.defaultTerm ?? null,
    defaultDownPayment: options.defaultDownPayment ?? null,
    rateMatrix: options.rateMatrix,
  };
}
